import type { FC } from 'react';

import { cx } from '@emotion/css';
import { Text } from '@livechat/design-system-react-components';
import Markdown, { type Options } from 'react-markdown';

import { type CopilotMessageEvent as CopilotMessageEventType } from 'store/entities/copilot/interfaces';

import { CopilotActionButton } from '../buttons/CopilotActionButton';
import { CopilotAssistantMessageBottomBar } from '../buttons/CopilotAssistantMessageBottomBar';

import { CopilotAgentMessage } from './CopilotAgentMessage';
import { CopilotLinkWithTooltip } from './CopilotLinkWithTooltip';

import * as styles from './styles';

interface IProps {
  event: CopilotMessageEventType;
  isAgentMessage: boolean;
}

const componentsConfig: Options['components'] = {
  a: ({ children, href }) => <CopilotLinkWithTooltip href={href}>{children}</CopilotLinkWithTooltip>,
  pre: ({ children }) => <>{children}</>,
};

export const CopilotMessageEvent: FC<IProps> = ({ event, isAgentMessage }) => {
  const isMessageWithButtons = event?.properties?.buttons?.length;
  const isWithHeader = !!event.header;
  const hasCancelledRequest = event?.properties?.hasCancelledRequest;

  const shouldDisplayBottomBar = !isAgentMessage && !event?.properties?.withoutActions;

  if (isAgentMessage) {
    return <CopilotAgentMessage text={event.text} />;
  }

  return (
    <div className={cx(styles.messageEventWrapper(), 'bubble-wrapper')}>
      <div className={styles.eventWrapper()} key={event.eventId}>
        <div className={cx('bubble', styles.bubble)}>
          <Text className={styles.eventText}>
            <div className={styles.outerWrapper}>
              {isWithHeader && <p className={styles.messageHeading}>{event.header}</p>}
              <Markdown
                className={cx(styles.markdown, {
                  [styles.cancelledMessage]: hasCancelledRequest,
                })}
                components={componentsConfig}
              >
                {event.text}
              </Markdown>
            </div>
          </Text>

          {isMessageWithButtons && (
            <div className={styles.buttonContainer}>
              {/* eslint-disable-next-line react/jsx-props-no-spreading */}
              {event.properties?.buttons?.map((button) => <CopilotActionButton key={button.id} {...button} />)}
            </div>
          )}
        </div>
      </div>
      {shouldDisplayBottomBar && <CopilotAssistantMessageBottomBar event={event} />}
    </div>
  );
};
